// "Inspired" by Bootstrap 5 dom/event-handler.js
// No namespaced events atm
// https://github.com/twbs/bootstrap/blob/fee563d1fca2acea0bde65ab382b4b79f93f600d/js/src/dom/event-handler.js

import { EVENT_MOUSEENTER, EVENT_MOUSELEAVE } from "./events";

type HandlerFn = (event: WithDelegateTarget) => void;

type WrappedHandlerFn = HandlerFn & {
    delegatedSelector?: string;
    once?: boolean;
    event?: string;
    callable?: HandlerFn;
};

interface WrappedHandlersMap {
    [eventType: string]: WrappedHandlerFn[],
}

export type WithDelegateTarget<T extends Event = Event | CustomEvent> = T & {
    delegateTarget?: EventTarget;
};

export class EventHandler {
    static #registry = new WeakMap<EventTarget, WrappedHandlersMap>();

    private constructor() {
    }

    static on(element: EventTarget, type: string, handler: HandlerFn): void;
    static on(element: EventTarget, type: string, selector: string, handler: HandlerFn): void;
    static on(element: EventTarget, type: string, handler: HandlerFn | string, delegatedHandler?: HandlerFn): void {
        this.#addHandler(element, type, handler, delegatedHandler, false);
    }

    static one(element: EventTarget, type: string, handler: HandlerFn): void;
    static one(element: EventTarget, type: string, selector: string, handler: HandlerFn): void;
    static one(element: EventTarget, type: string, handler: HandlerFn | string, delegatedHandler?: HandlerFn): void {
        this.#addHandler(element, type, handler, delegatedHandler, true);
    }

    static off(element: EventTarget, type: string, handler: HandlerFn): void;
    static off(element: EventTarget, type: string, selector: string, handler: HandlerFn): void;
    static off(element: EventTarget, type: string, handler: HandlerFn | string, delegatedHandler?: HandlerFn): void {
        const [isDelegated, callable] = this.#getDelegationParams(handler, delegatedHandler);
        const events = this.#getEvents(element);
        const listeners = events[type] || [];

        if (callable) {
            this.#removeHandler(element, events, type, callable, isDelegated ? <string>handler : null);

            return;
        }

        listeners.forEach((fn) => {
            this.#removeHandler(element, events, type, fn.callable, fn.delegatedSelector);
        });
    }

    static trigger(element: EventTarget, type: string): Event;
    static trigger(element: EventTarget, type: string, detail: any): CustomEvent;
    static trigger(element: EventTarget, type: string, detail?: any): Event | CustomEvent {
        let event;

        if (detail) {
            event = new CustomEvent(type, {
                bubbles: true,
                cancelable: true,
                detail
            });
        } else {
            event = new Event(type, {
                bubbles: true,
                cancelable: true,
            });
        }

        element.dispatchEvent(event);

        return event;
    }

    static #addHandler(element: EventTarget, types: string, handler: HandlerFn | string, delegatedHandler?: HandlerFn, once: boolean = false): void {
        types.trim().split(/\s+/).forEach((type) => {
            let [isDelegated, callable] = this.#getDelegationParams(handler, delegatedHandler);

            if (isDelegated && (type === EVENT_MOUSEENTER || type === EVENT_MOUSELEAVE)) {
                callable = ((fn) => {
                    return function (event: WithDelegateTarget<MouseEvent>) {
                        if (!event.relatedTarget || (event.relatedTarget !== event.delegateTarget && !(<HTMLElement>event.delegateTarget).contains(<HTMLElement>event.relatedTarget))) {
                            fn.call(this, event);
                        }
                    };
                })(callable);
            }

            const fn = isDelegated ?
                this.#makeDelegatedHandler(element, <string>handler, callable) :
                this.#makeHandler(element, callable);

            fn.delegatedSelector = isDelegated ? <string>handler : null;
            fn.callable = callable;
            fn.once = once;
            fn.event = types;

            const events = this.#getEvents(element);
            events[type] = events[type] || [];
            events[type].push(fn);

            element.addEventListener(type, fn, isDelegated);
        });
    }

    static #removeHandler(element: EventTarget, events: WrappedHandlersMap, types: string, handler: HandlerFn, selector?: string): void {
        types.trim().split(/\s+/).forEach((type) => {
            const [i, fn] = this.#findHandler(events[type], handler, selector);

            if (!fn) {
                return;
            }

            element.removeEventListener(type, fn, Boolean(selector));
            events[type].splice(i, 1);
        });
    }

    static #getDelegationParams(handler: HandlerFn | string, delegatedHAndler?: HandlerFn): [boolean, HandlerFn] {
        const isDelegated = typeof handler === 'string';

        return [isDelegated, isDelegated ? delegatedHAndler : <HandlerFn>handler];
    }

    static #makeHandler(element: EventTarget, fn: HandlerFn): WrappedHandlerFn {
        const handler: WrappedHandlerFn = (event: WithDelegateTarget<Event>) => {
            event.delegateTarget = element;

            if (handler.once) {
                this.off(element, handler.event, fn);
            }

            fn.apply(element, [event]);
        };

        return handler;
    }

    static #makeDelegatedHandler(element: EventTarget, selector: string, fn: HandlerFn): WrappedHandlerFn {
        const handler: WrappedHandlerFn = (event: WithDelegateTarget<Event>) => {
            const nodes = document.querySelectorAll(selector);

            for (let target = <HTMLElement>event.target; target !== null && target !== element; target = target.parentElement) {
                for (const node of nodes) {
                    if (node !== target) {
                        continue;
                    }

                    event.delegateTarget = target;

                    if (handler.once) {
                        this.off(element, handler.event, selector, fn);
                    }

                    fn.apply(element, [event]);

                    return;
                }
            }
        };

        return handler;
    }

    static #findHandler(events: WrappedHandlerFn[], callable: HandlerFn, selector?: string): [number, WrappedHandlerFn | null] {
        const index = events.findIndex((fn) => (
            fn.callable === callable && fn.delegatedSelector === selector
        ));

        return [index, events[index]];
    }

    static #getEvents(element: EventTarget): WrappedHandlersMap {
        if (!this.#registry.has(element)) {
            this.#registry.set(element, {});
        }

        return this.#registry.get(element);
    }
}
